<template>
  <div class="body bank-app" :dir="isRTL ? 'rtl' : 'ltr'">
    
    <Navbar 
      :showBackButton="true" 
      @back="goBack"
    />

    <div class="main-content">

      <!-- Account Summary Section -->
      <div class="account-summary-dashboard">

        <div class="account-type">
          {{ $t('dashboard.accountBalance') }}
        </div>

        <div class="account-balance">
          <span class="balance-label"></span>
          <span class="balance-amount">
            {{ formatRemainingCash(remainingCash) }} {{ formatBestanKari(remainingCash) }}
          </span>
        </div>

      </div>


      <!-- Card Services Section -->
      <div class="horizontal-services-section">

        <h3 class="section-title">
          {{ $t('dashboard.commonServices') }}
        </h3>

        <div class="horizontal-scroll-container">
          <div class="scroll-content">

            <div v-if="hasPermission('Factors')" class="scroll-service-item" @click="navigateToFactors">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/invoice.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.invoiceList') }}
              </span>
            </div>

            <div v-if="hasPermission('TahoodY')" class="scroll-service-item" @click="navigateToTaahodatY">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/commitment.png" />
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.yourCommitments') }}
              </span>
            </div>

            <div v-if="hasPermission('TahoodO')" class="scroll-service-item" @click="navigateToTaahodatO">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/commitment.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.ourCommitments') }}
              </span>
            </div>

          </div>
        </div>

        <div class="horizontal-scroll-container">
          <div class="scroll-content">

            <div v-if="hasPermission('KalaO')" class="scroll-service-item" @click="navigateToKalaO">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/product.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.ourConsignmentGoods') }}
              </span>
            </div>

            <div v-if="hasPermission('KalaY')" class="scroll-service-item" @click="navigateToKalaY">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/product.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.othersConsignmentGoods') }}
              </span>
            </div>

            <div v-if="hasPermission('Vajeh')" class="scroll-service-item" @click="openRequestModal">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/funds.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.requestMoney') }}
              </span>
            </div>

          </div>
        </div>

        <div class="horizontal-scroll-container">
          <div class="scroll-content">

            <div v-if="hasPermission('MyOrders')" class="scroll-service-item" @click="navigateToInvoiceList">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/delivery.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.invoicesList') }}
              </span>
            </div>

            <div v-if="hasPermission('Pay')" class="scroll-service-item" @click="openPayMoneyModal">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/funds.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.payMoney') }}
              </span>
            </div>

            <div class="scroll-service-item" @click="navigateToRequestChek">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/funds.png"/>
              </div>
              <span class="scroll-service-label">
                {{ $t('dashboard.requestChek') }}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>


    <!-- Commercial Details Modal -->

    <div v-if="showCommercialModal" class="modal-overlay" @click="closeCommercialModal">

      <div class="modal-content" @click.stop>

        <div class="modal-header">

          <h3>{{ $t('commercial.title') }}</h3>

          <div class="modal-actions">

            <button 
              v-if="commercialDetails.length > 0 && !commercialLoading && !commercialError"
              class="print-modal-btn"
              @click="printCommercialDetails"
            >
              🖨️ {{ $t('common.print') }}
            </button>

            <button class="close-btn" @click="closeCommercialModal">
              ×
            </button>

          </div>

        </div>


        <div class="modal-body">

          <!-- Loading -->

          <div v-if="commercialLoading" class="modal-loading">
            <div class="loading-spinner"></div>
            <span>{{ $t('commercial.loading') }}</span>
          </div>


          <!-- Error -->

          <div v-else-if="commercialError" class="modal-error">
            <div class="error-icon">⚠️</div>
            <h4>{{ $t('commercial.errorTitle') }}</h4>
            <p>{{ commercialError }}</p>

            <button class="retry-btn" @click="retryFetchCommercialDetails">
              {{ $t('common.retry') }}
            </button>
          </div>


          <!-- Success -->

          <div v-else-if="commercialDetails.length > 0" class="commercial-details">

            <div class="details-grid">

              <div 
                v-for="(detail, index) in commercialDetails" 
                :key="index"
                class="detail-card"
              >

                <!-- Product Section -->

                <div class="detail-section">

                  <h4 class="section-title">
                    {{ $t('commercial.productTitle') }}
                  </h4>

                  <div class="info-box">

                    <div class="info-row">
                      <span class="info-label">
                        {{ $t('commercial.productName') }}
                      </span>

                      <span class="info-value">
                        {{ detail.Name || $t('common.unknown') }}
                      </span>
                    </div>

                    <div class="info-row">
                      <span class="info-label">
                        {{ $t('commercial.unit') }}
                      </span>

                      <span class="info-value">
                        {{ detail.Vahed || $t('common.unknown') }}
                      </span>
                    </div>

                  </div>

                </div>


                <!-- Values -->

                <div class="detail-section">

                  <h4 class="section-title">
                    {{ $t('commercial.values') }}
                  </h4>

                  <div class="info-box">

                    <div class="info-row" v-if="detail.Value2">
                      <span class="info-label">{{ $t('commercial.value2') }}</span>
                      <span class="info-value">{{ formatNumber(detail.Value2) }}</span>
                    </div>

                    <div class="info-row" v-if="detail.Value3">
                      <span class="info-label">{{ $t('commercial.value3') }}</span>
                      <span class="info-value">{{ formatNumber(detail.Value3) }}</span>
                    </div>

                    <div class="info-row" v-if="detail.Value4">
                      <span class="info-label">{{ $t('commercial.value4') }}</span>
                      <span class="info-value">{{ formatNumber(detail.Value4) }}</span>
                    </div>

                    <div class="info-row" v-if="detail.GM7">
                      <span class="info-label">{{ $t('commercial.grossWeight') }}</span>
                      <span class="info-value">{{ formatNumber(detail.GM7) }}</span>
                    </div>

                    <div class="info-row" v-if="detail.GM8">
                      <span class="info-label">{{ $t('commercial.emptyWeight') }}</span>
                      <span class="info-value">{{ formatNumber(detail.GM8) }}</span>
                    </div>

                  </div>

                </div>


                <!-- Amounts -->

                <div class="detail-section">

                  <h4 class="section-title">
                    {{ $t('commercial.amounts') }}
                  </h4>

                  <div class="info-box">

                    <div class="info-row">
                      <span class="info-label">{{ $t('commercial.unitPrice') }}</span>
                      <span class="info-value amount">{{ formatCurrency(detail.Fe) }}</span>
                    </div>

                    <div class="info-row">
                      <span class="info-label">{{ $t('commercial.totalAmount') }}</span>
                      <span class="info-value amount total">{{ formatCurrency(detail.Money) }}</span>
                    </div>

                  </div>

                </div>

              </div>
            </div>
          </div>


          <!-- Empty -->

          <div v-else class="modal-empty">
            <div class="empty-icon">📦</div>
            <h4>{{ $t('commercial.emptyTitle') }}</h4>
            <p>{{ $t('commercial.emptyDesc') }}</p>
          </div>

        </div>


        <div class="modal-footer">
          <button class="close-modal-btn" @click="closeCommercialModal">
            {{ $t('common.close') }}
          </button>
        </div>

      </div>
    </div>


    <!-- Pay Money Modal -->
    <div v-if="showPayMoneyModal" class="modal-overlay" @click="showPayMoneyModal = false">
      <div class="request-modal pay-money-modal" @click.stop>
        <div class="modal-header">
          <h3 style="color: var(--text);">{{ $t('paymentOrder.title') }}</h3>
          <button class="close-btn" @click="showPayMoneyModal = false">×</button>
        </div>

        <div class="modal-body">
          <!-- Customer Status Display -->
          <div class="customer-status" :class="customerStatusClass">
            <div class="status-icon">{{ customerStatusIcon }}</div>
            <div class="status-info">
              <span class="status-label">{{ userFirstName }}</span>
            </div>
            <div class="status-amount">{{ formatCurrency(Math.abs(remainingCash)) }} {{ formatBestanKari(remainingCash) }}</div>
          </div>

          <div class="form-row">
            <label>{{ $t('paymentOrder.date') }}</label>
            <input v-model="requestForm.date" type="text" readonly class="readonly-input" />
          </div>

          <div class="form-row">
            <label>{{ $t('paymentOrder.amount') }}</label>
            <div class="amount-input-wrapper">
              <input 
                :value="formattedAmount"
                @input="updateAmount($event.target.value)"
                type="text"
                :placeholder="$t('paymentOrder.enterAmount')"
                class="amount-input"
                :readonly="isBesCustomer"
                :class="{ 'readonly-amount': isBesCustomer }"
              />
              <span class="currency-label">ریال</span>
            </div>
            <div v-if="amountInWords" class="amount-in-words" dir="rtl">
              مبلغ به حروف: {{ amountInWords }} ریال
            </div>
            <div v-if="isBesCustomer" class="amount-hint">
              <span class="hint-icon">ℹ️</span>
              <span>{{ $t('paymentOrder.besAmountHint') }}</span>
            </div>
          </div>

          <div class="form-row">
            <label>{{ $t('paymentOrder.reason') }}</label>
            <select v-model="requestForm.reason" class="reason-select">
              <option value="prior_year_debt">{{ $t('paymentOrder.reasonPriorYearDebt') }}</option>
              <option value="current_year_tuition_cash">{{ $t('paymentOrder.reasonCurrentYearTuitionCash') }}</option>
              <option value="current_year_returned_check">{{ $t('paymentOrder.reasonCurrentYearReturnedCheck') }}</option>
              <option value="buy">{{ $t('paymentOrder.reasonAccountCause') }}</option>
              <option value="settlement">{{ $t('paymentOrder.reasonOverdueFunds') }}</option>
              <option value="consignment">{{ $t('paymentOrder.reasonConsignmentGoods') }}</option>
              <option value="other">{{ $t('common.other') }}</option>
            </select>
          </div>

          <!-- <div class="form-row">
            <label>{{ $t('paymentOrder.payee') }}</label>
            <input v-model="requestForm.name" type="text" readonly class="readonly-input" />
          </div> -->

          <!-- <div class="form-row" v-if="showCustomReason">
            <label>{{ $t('paymentOrder.customReason') }}</label>
            <textarea 
              v-model="requestForm.customReason" 
              rows="3"
              :placeholder="$t('paymentOrder.customReasonPlaceholder')"
              class="custom-reason-input"
            ></textarea>
          </div> -->

          <div class="form-row">
            <label>{{ $t('paymentOrder.userDescription') }}</label>
            <textarea
              v-model="requestForm.userDescription"
              rows="3"
              maxlength="300"
              :placeholder="$t('paymentOrder.userDescriptionPlaceholder')"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer1">
          <button class="cancel-btn1" @click="showPayMoneyModal = false">
            {{ $t('common.cancel') }}
          </button>
          <button class="submit-btn1" @click="submitRequestMoney" :disabled="!canSubmit || paymentSubmitting">
            {{ $t('paymentOrder.goToDargah') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Request Money Modal -->
    <div v-if="showRequestModal" class="modal-overlay" @click="showRequestModal = false">

      <div class="request-modal" @click.stop>

        <div class="modal-header">

          <h3 style="color: var(--text);">{{ $t('paymentOrder.title') }}</h3>

          <button class="close-btn" @click="showRequestModal = false">
            ×
          </button>

        </div>

        <div class="modal-body">

          <div class="form-row">
            <label>{{ $t('paymentOrder.date') }}</label>
            <input v-model="requestForm.date" type="text" readonly />
          </div>

          <div class="form-row">
            <label>{{ $t('paymentOrder.amount') }}</label>

            <input 
              :value="formattedAmount"
              @input="updateAmount($event.target.value)"
              type="text"
              :placeholder="$t('paymentOrder.enterAmount')"
              class="amount-input"
            />
          </div>

          <div class="form-row">

            <label>{{ $t('paymentOrder.reason') }}</label>

            <select v-model="requestForm.reason">

              <option value="prior_year_debt">
                {{ $t('paymentOrder.reasonPriorYearDebt') }}
              </option>

              <option value="current_year_tuition_cash">
                {{ $t('paymentOrder.reasonCurrentYearTuitionCash') }}
              </option>

              <option value="current_year_returned_check">
                {{ $t('paymentOrder.reasonCurrentYearReturnedCheck') }}
              </option>

              <option value="buy">
                {{ $t('paymentOrder.reasonAccountCause') }}
              </option>

              <option value="settlement">
                {{ $t('paymentOrder.reasonOverdueFunds') }}
              </option>

              <option value="consignment">
                {{ $t('paymentOrder.reasonConsignmentGoods') }}
              </option>

              <option value="other">
                {{ $t('common.other') }}
              </option>

            </select>

          </div>

          <!-- Payee field hidden from the Request Pay modal as requested.
          <div class="form-row">
            <label>{{ $t('paymentOrder.payee') }}</label>
            <input v-model="requestForm.name" type="text" readonly />
          </div>
          -->

          <div class="form-row">
            <label>{{ $t('paymentOrder.userDescription') }}</label>
            <textarea
              v-model="requestForm.userDescription"
              rows="3"
              maxlength="300"
              :placeholder="$t('paymentOrder.userDescriptionPlaceholder')"
            ></textarea>
          </div>

        </div>

        <div class="modal-footer">
          <button class="submit-btn" @click="submitRequestMoney">
            {{ $t('paymentOrder.submitRequest') }}
          </button>
        </div>

      </div>
    </div>


    <div v-if="gatewayToastVisible" class="gateway-toast" role="status" aria-live="polite">
      {{ gatewayToastMessage }}
    </div>
<!-- 
    <div style="z-index:999999">

      <ChatWidget 
        v-if="showChat"
        department="support"
        :company-id="selectedCompanyId"
        :position="{ bottom: '20px', right: '20px' }"
      />

    </div> -->

  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { accountingService, operatorService, api } from '@/services/api'
import { messengerService } from '@/services/apiMessanger'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import ChatWidget from '../Messanger/ChatWidget.vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'BankDashboard',
  components: {
    Navbar,
    ChatWidget
  },
  setup() {
    const authStore = useAuthStore()
    const remainingCash = ref(0)
    const accountingData = ref([])
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const currentPage = ref(1)
    const showCommercialModal = ref(false)
    const commercialDetails = ref([])
    const commercialLoading = ref(false)
    const commercialError = ref(null)
    const selectedInvoice = ref(null)
    const router = useRouter()
    const showRequestModal = ref(false)
    const showPayMoneyModal = ref(false)
    const paymentSubmitting = ref(false)
    const gatewayToastVisible = ref(false)
    const gatewayToastMessage = ref('')
    let gatewayToastTimer

    const isOperator = ref(false)
    const operatorData = ref(null)
    const operatorStatus = ref('offline')
    const pendingCount = ref(0)
    const checkingOperator = ref(true)
    const { t } = useI18n()

    const showGatewayToast = (message) => {
      gatewayToastMessage.value = message
      gatewayToastVisible.value = true
      clearTimeout(gatewayToastTimer)
      gatewayToastTimer = setTimeout(() => { gatewayToastVisible.value = false }, 3500)
    }

    const showChat = computed(() => {
      // Show chat widget based on user role/needs
      return true
    })

    const hasPermission = (permissionKey) => {
      console.log(authStore.permissions)
      return authStore.permissions.includes(permissionKey)
    }

    // Check if user is operator
    const checkOperatorStatus = async () => {
      checkingOperator.value = true
      try {
        const response = await operatorService.checkIfOperator()
        console.log('Operator check response:', response)
        
        if (response.data?.success) {
          isOperator.value = response.data.is_operator
          if (response.data.is_operator && response.data.operator_data) {
            operatorData.value = response.data.operator_data
            
            // Also fetch operator status from messenger
            await fetchOperatorStatus()
            await fetchPendingCount()
          }
        }
      } catch (error) {
        console.error('Error checking operator status:', error)
        isOperator.value = false
      } finally {
        checkingOperator.value = false
      }
    }

const isBesCustomer = computed(() => {
  return remainingCash.value > 0
})

const isBedCustomer = computed(() => {
  return remainingCash.value < 0
})

const customerStatusClass = computed(() => {
  if (isBesCustomer.value) return 'status-bes'
  if (isBedCustomer.value) return 'status-bed'
  return 'status-zero'
})

const customerStatusIcon = computed(() => {
  return '💳'
})

const customerStatusText = computed(() => {
  if (isBesCustomer.value) return t('paymentOrder.besStatus')
  if (isBedCustomer.value) return t('paymentOrder.bedStatus')
  return t('paymentOrder.zeroStatus')
})

const showCustomReason = computed(() => {
  return requestForm.value.reason === 'other'
})

const paymentReasonBabat = {
  prior_year_debt: 'بدهی از سال قبل',
  current_year_tuition_cash: 'نقدی شهریه امسال',
  current_year_returned_check: 'چک برگشتی امسال',
}

const canSubmit = computed(() => {
  if (!requestForm.value.amount) return false
  const amountNumber = parseInt(requestForm.value.amount.replace(/,/g, ''))
  if (isNaN(amountNumber) || amountNumber <= 0) return false
  
  // If BES customer, amount must be exactly the remaining cash
  if (isBesCustomer.value) {
    return amountNumber === Math.abs(remainingCash.value)
  }
  
  return true
})

// Update the openRequestModal method
const openPayMoneyModal = () => {
  const today = new Date().toLocaleDateString('fa-IR')
  requestForm.value.date = today
  requestForm.value.name = JSON.parse(localStorage.getItem('user'))?.name || "نامشخص"
  
  // Set amount based on customer status
  if (isBesCustomer.value) {
    // BES: Set to remaining cash amount (they should pay this)
    requestForm.value.amount = Math.abs(remainingCash.value).toString()
  } else {
    // BED or Zero: Allow manual entry
    requestForm.value.amount = ''
  }
  
  requestForm.value.reason = 'current_year_tuition_cash'
  requestForm.value.customReason = ''
  requestForm.value.userDescription = ''
  showPayMoneyModal.value = true
}

const openRequestModal = () => {
  const today = new Date().toLocaleDateString('fa-IR')

  requestForm.value.date = today
  console.log(JSON.parse(localStorage.getItem('user')))
  requestForm.value.name = JSON.parse(localStorage.getItem('user')).name || "نامشخص"
  requestForm.value.amount = isBedCustomer.value
    ? Math.abs(remainingCash.value).toString()
    : ''
  requestForm.value.userDescription = ''
  showRequestModal.value = true
}

// Update the submitRequestMoney method
const submitPayMoney = () => {
  if (!requestForm.value.amount) {
    alert("مبلغ را وارد کنید")
    return
  }

  // Convert amount to number for submission
  const amountNumber = parseInt(requestForm.value.amount.replace(/,/g, ''))
  
  if (isNaN(amountNumber) || amountNumber <= 0) {
    alert("لطفاً مبلغ معتبر وارد کنید")
    return
  }

  // If BES customer, validate amount matches remaining cash
  if (isBesCustomer.value && amountNumber !== Math.abs(remainingCash.value)) {
    alert(`مبلغ باید برابر با ${formatCurrency(Math.abs(remainingCash.value))} باشد`)
    return
  }

  console.log("submitted:", {
    ...requestForm.value,
    amount: amountNumber,
    customerStatus: isBesCustomer.value ? 'bes' : 'bed'
  })

  alert(`درخواست مبلغ ${formatCurrency(amountNumber)} با موفقیت ثبت شد`)
  showPayMoneyModal.value = false
}

const submitRequestMoney = async () => {
  if (paymentSubmitting.value) return
  const amountNumber = parseInt(String(requestForm.value.amount || '').replace(/,/g, ''), 10)
  if (!Number.isSafeInteger(amountNumber) || amountNumber <= 0 || !canSubmit.value) {
    alert('Please enter a valid amount')
    return
  }

  paymentSubmitting.value = true
  try {
    const randomSuffix = String(Math.floor(Math.random() * 1000)).padStart(3, '0')
    const orderId = Number(`${Date.now()}${randomSuffix}`)
    const launchResponse = await api.post('/payment/create-launch', {
      company_id: Number(selectedCompanyId.value),
      amount: amountNumber,
      order_id: orderId,
      description: requestForm.value.reason === 'other'
        ? requestForm.value.customReason
        : (paymentReasonBabat[requestForm.value.reason] || requestForm.value.reason),
      user_description: String(requestForm.value.userDescription || '').trim(),
    })
    if (!launchResponse.data?.success || !launchResponse.data.launch_token || !launchResponse.data.gateway_url) {
      throw new Error('Could not authorize this payment')
    }

    const gatewayUrl = new URL(launchResponse.data.gateway_url)
    if (gatewayUrl.protocol !== 'https:') throw new Error('Payment gateway must use HTTPS')
    gatewayUrl.hash = encodeURIComponent(launchResponse.data.launch_token)
    window.location.assign(gatewayUrl.toString())
  } catch (error) {
    paymentSubmitting.value = false
    if (error.response?.data?.detail?.code === 'payment_gateway_missing') {
      showGatewayToast(t('paymentOrder.noGateway'))
      return
    }
    alert(error.response?.data?.detail || error.message || 'Could not connect to the payment service')
  }
}

const userFirstName = computed(() => {
    console.log('Current user:', authStore.user)

    return authStore.user?.name
})

    // Fetch operator status from messenger
    const fetchOperatorStatus = async () => {
      if (!operatorData.value) return
      
      try {
        // You might need to implement this in messengerService
        const statusResponse = await messengerService.getOperatorStats(operatorData.value.operator_id)
        if (statusResponse.status === 'success') {
          operatorStatus.value = statusResponse.status
        }
      } catch (error) {
        console.error('Error fetching operator status:', error)
      }
    }

    // Fetch pending questions count
    const fetchPendingCount = async () => {
      if (!operatorData.value) return
      
      try {
        const response = await messengerService.getPendingQuestions()
        if (response.status === 'success') {
          // Filter by operator's categories or just show total
          pendingCount.value = response.questions?.length || 0
        }
      } catch (error) {
        console.error('Error fetching pending count:', error)
      }
    }

    // Navigate to operator panel
    const navigateToOperatorPanel = () => {
      if (!operatorData.value) return
      
      // Store operator data in localStorage or pass via router
      localStorage.setItem('operatorData', JSON.stringify(operatorData.value))
      
      // Navigate to operator panel with the first category or default
      const defaultDepartment = operatorData.value.categories?.[0]?.type || 'support'
      router.push({
        path: '/operator-panel',
        query: {
          department: defaultDepartment,
          companyId: operatorData.value.company_id,
          companyName: operatorData.value.company_name
        }
      })
    }

    const requestForm = ref({
      date: '',
      amount: '',
      reason: '',
      name: '',
      userDescription: ''
    })

  // Computed property for formatted amount display
  const formattedAmount = computed({
    get: () => {
      if (!requestForm.value.amount) return ''
      // Format the number with 3-digit separators
      return requestForm.value.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    set: (newValue) => {
      // Remove all non-digit characters except commas
      const rawValue = newValue.replace(/[^\d]/g, '')
      requestForm.value.amount = rawValue
    }
  })

  const amountInWords = computed(() => {
    const raw = String(requestForm.value.amount || '').replace(/\D/g, '')
    if (!raw) return ''
    const value = Number(raw)
    if (!Number.isSafeInteger(value) || value <= 0) return ''

    const belowTwenty = ['صفر', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده']
    const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود']
    const hundreds = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد']
    const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون', 'کوادریلیون']

    const groupToWords = (group) => {
      const parts = []
      if (group >= 100) parts.push(hundreds[Math.floor(group / 100)])
      const remainder = group % 100
      if (remainder > 0 && remainder < 20) parts.push(belowTwenty[remainder])
      else if (remainder >= 20) {
        parts.push(tens[Math.floor(remainder / 10)])
        if (remainder % 10) parts.push(belowTwenty[remainder % 10])
      }
      return parts.join(' و ')
    }

    const groups = []
    let remaining = value
    while (remaining > 0) {
      groups.push(remaining % 1000)
      remaining = Math.floor(remaining / 1000)
    }
    return groups
      .map((group, index) => group ? `${groupToWords(group)}${scales[index] ? ` ${scales[index]}` : ''}` : '')
      .filter(Boolean)
      .reverse()
      .join(' و ')
  })

  // Method to handle input changes
  const updateAmount = (value) => {
    // Remove all non-digit characters
    const rawValue = value.replace(/[^\d]/g, '')
    requestForm.value.amount = rawValue
  }

    const navigateToFactors = () => {
      router.push('/factors')
    }

    const navigateToTaahodatY = () => {
      router.push('/taahodat-y')
    }

    const navigateToTaahodatO = () => {
      router.push('/taahodat-O')
    }

    const navigateToPeak = () => {
      router.push('/peak/customer-delivery')
    }

    const navigateToKalaY = () => {
      router.push('/kala-y')
    }

    const navigateToKalaO = () => {
      router.push('/kala-O')
    }

    const navigateToInvoiceList = () => {
      router.push('/invoice-list')
    }

    const navigateToRequestChek = () => {
      router.push('/check-transfer')
    }

    const goBack = () => {
      router.push('/select-role')
    }

    const formatRemainingCash = (amount) => {
      if (!amount && amount !== 0) return '۰'
      // Use absolute value for display
      const absoluteAmount = Math.abs(amount)
      return new Intl.NumberFormat('fa-IR').format(absoluteAmount).replace('ریال', '') + ' ریال'
    }

    const isAuthenticated = computed(() => authStore.isAuthenticated)

    // Fetch accounting data and remaining cash
    const fetchAccountingData = async () => {
      if (!authStore.user?.customerId) {
        console.error('No customer ID found')
        return
      }

      loading.value = true
      error.value = null

      try {
        console.log('Fetching data for customer ID:', authStore.user.subCodeId)
        
        // Fetch remaining cash
        const cashResponse = await accountingService.getRemainingCash()
        if (cashResponse.data.success) {
          remainingCash.value = cashResponse.data.remaining_cash || 0
        }
        console.log('Remaining cash:', remainingCash.value)

      } catch (err) {
        console.error('Error fetching accounting data:', err)
        error.value = err.response?.data?.detail || 'خطا در دریافت اطلاعات'
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR', {
        style: 'currency',
        currency: 'IRR'
      }).format(amount).replace('ریال', '') + ' ریال'
    }

    const formatBestanKari = (amount) => {
      if (!amount && amount !== 0) return '۰'
      if (amount > 0){
        return 'بدهکار'
      }else {
        return 'بستانکار'
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      try {
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('fa-IR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date)
      } catch {
        return dateString
      }
    }

    const formatPersianDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      
      try {
        // Split the date string (format: 1404/06/18)
        const parts = dateString.split('/')
        if (parts.length !== 3) return dateString
        
        const year = parts[0]
        const month = parseInt(parts[1])
        const day = parts[2]
        
        // Persian month names
        const persianMonths = [
          'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
          'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]
        
        if (month >= 1 && month <= 12) {
          return `${day} ${persianMonths[month - 1]} ${year}`
        }
        
        return dateString
      } catch {
        return dateString
      }
    }

    const hasAdditionalInfo = (detail) => {
      return (detail.Comment2 && detail.Comment2 !== '-') || 
             (detail.Comment && detail.Comment !== '-') || 
             detail.Codeing
    }

    const formatNumber = (number) => {
      if (!number && number !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    }

    const selectedCompanyId = computed(() => {
      // Get the selected company from auth store or localStorage
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany') || '{}')
      return selectedCompany.company_id || authStore.getSelectedCompany?.company_id || 1 // Default to 1
    })

    onMounted(async () => {
      if (isAuthenticated.value) {
        console.log("User is authenticated, fetching accounting data...")
        await fetchAccountingData()
        //await checkOperatorStatus()
      }
    })

    return {
      remainingCash,
      accountingData,
      loading,
      error,
      searchQuery,
      currentPage,
      isAuthenticated,
      showCommercialModal,
      commercialDetails,
      commercialLoading,
      commercialError,
      selectedInvoice,
      showRequestModal,
      requestForm,
      formattedAmount,
      amountInWords,
      updateAmount,
      openRequestModal,
      submitRequestMoney,
      formatRemainingCash,
      formatCurrency,
      formatBestanKari,
      formatPersianDate,
      formatDate,
      hasAdditionalInfo,
      formatNumber,
      navigateToFactors,
      navigateToTaahodatO,
      navigateToPeak,
      navigateToTaahodatY,
      navigateToKalaO,
      navigateToKalaY,
      navigateToOperatorPanel,
      navigateToInvoiceList,
      navigateToRequestChek,
      isOperator,
      showChat,
      selectedCompanyId,
      checkOperatorStatus,
      goBack,
      hasPermission,
      isBesCustomer,
      isBedCustomer,
      customerStatusClass,
      customerStatusIcon,
      customerStatusText,
      showCustomReason,
      canSubmit,
      openPayMoneyModal,
      submitPayMoney,
      showPayMoneyModal,
      userFirstName,
      gatewayToastVisible,
      gatewayToastMessage,

      truncateText: (text, maxLength) => {
        if (!text) return ''
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
      }
    }
  }
}
</script>

<style scoped>
.gateway-toast {
  position: fixed;
  z-index: 1000000;
  inset-inline: 16px;
  top: max(16px, env(safe-area-inset-top));
  width: fit-content;
  max-width: min(520px, calc(100vw - 32px));
  margin-inline: auto;
  padding: 14px 18px;
  border: 1px solid #efb5b5;
  border-radius: 12px;
  background: #fff4f4;
  color: #8e2424;
  box-shadow: 0 8px 28px rgb(0 0 0 / 16%);
  text-align: center;
}

.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text);
  direction: rtl;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 80px; /* Space for bottom navigation */
}

.account-summary-dashboard {
  background: linear-gradient(135deg, #4a6baf, #2c4a8c);
  color: var(--text);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}

.section-title {
  font-size: 16px;
  margin: 20px 0 12px;
  color: var(--text);
  font-weight: 600;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.service-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4a6baf;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.service-label {
  font-size: 12px;
  color: #555;
}

.additional-text {
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  color: #555;
}

.account-number {
  margin-top: 8px;
  font-weight: bold;
  color: #4a6baf;
}

/* Horizontal Scroll Services */
.horizontal-services-section {
  margin-bottom: 24px;
}

.horizontal-scroll-container {
  overflow-x: auto;
  padding: 8px 0;
  margin: 0 -16px;
  padding: 8px 16px;
}

.horizontal-scroll-container::-webkit-scrollbar {
  display: none;
}

.scroll-content {
  display: flex;
  gap: 16px;
  padding: 4px 0;
  min-width: min-content;
}

.scroll-service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
  flex-shrink: 0;
  margin-right: auto;
  margin-left: auto;
}

.scroll-service-item:hover {
  transform: scale(1.2);
  cursor: pointer;
  background-color: var(--hover);
  transform: translateY(-2px);
}

.scroll-service-item:active {
  transform: translateY(0);
}

.scroll-service-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background:var(--card-bg);
  border:1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #4a6baf;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden; /* Prevent image overflow */
}

.scroll-service-icon img {
  max-width: 70%; /* Limit image to 70% of container */
  max-height: 70%; /* Limit image to 70% of container */
  object-fit: contain; /* Maintain aspect ratio */
}

.scroll-service-label {
  font-size: 11px;
  color: var(--text);
  line-height: 1.3;
  max-width: 80px;
}

/* Original Services List */
.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-card {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.service-card .card-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  font-size: 12px;
  color: #4a6baf;
  font-weight: bold;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  background-color: white;
  padding: 12px 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  color: #555;
}

.nav-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  background-color: #e9ecef;
  border-radius: 50%;
}

.home-icon {
  background-color: #4a6baf;
}

.services-icon, .cards-icon, .profile-icon {
  background-color: #e9ecef;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .services-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  
  .service-icon {
    width: 50px;
    height: 50px;
    font-size: 10px;
  }
  
  .service-label {
    font-size: 10px;
  }

  .scroll-service-icon {
    width: 55px;
    height: 55px;
    font-size: 11px;
  }

  .scroll-service-label {
    font-size: 10px;
    max-width: 70px;
  }
}

.accounting-section {
  margin-bottom: 24px;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Existing styles remain below */
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text);
  direction: rtl;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 80px; /* Space for bottom navigation */
}

.account-summary-dashboard {
  background: var(--card-bg);
  border:1px solid var(--border);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.9;
  color:var(--text-muted);
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
}
/* جزییات Button Styles */
.accounting-card .card-body button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
}

.accounting-card .card-body button:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.accounting-card .card-body button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.accounting-card .card-body button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.request-modal {

  background: var(--bg);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.25);
  direction: rtl;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: var(--opposite-bg);
}

.form-row {
  margin: 12px 0;
  display: flex;
  flex-direction: column;
}

.form-row label {
  margin-bottom: 4px;
  font-weight: bold;
}

.form-row input,
.form-row select {
  padding: 10px;
  border:1px solid var(--border);
  background:var(--bg);
  color:var(--text);
    border-radius: 8px;
  font-size: 14px;
}

.modal-footer {
  text-align: center;
  margin-top: 15px;
}

.submit-btn {
  background: #2b57ff;
  color: white;
  padding: 10px 22px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 15px;
}
.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}

.btn-primary{
  background:var(--primary);
  color:#fff;
  border:none;
  padding:8px 14px;
  border-radius:6px;
  cursor:pointer;
}

.btn-secondary{
  background:var(--card-bg);
  border:1px solid var(--border);
  padding:8px 14px;
  border-radius:6px;
  cursor:pointer;
}

/* Request Money Modal Styles */
.request-modal {

  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  direction: rtl;
  max-height: 90vh;
  overflow-y: auto;
}

/* Customer Status Display */
.customer-status {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  background: var(--bg);
  border: 1px solid var(--border);
  transition: all 0.3s ease;
  margin-top: 15px;
}

.customer-status.status-bes {
  border-right: 4px solid #f59e0b;
  background: #fffbeb;
}

.customer-status.status-bed {
  border-right: 4px solid #10b981;
  background: #ecfdf5;
}

.customer-status.status-zero {
  border-right: 4px solid #6b7280;
  background: #f9fafb;
}

.status-icon {
  font-size: 28px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--card-bg);
  flex-shrink: 0;
}

.status-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.status-label {
  font-size: 15px;
  color: var(--text-muted);
}

.status-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}

.status-amount {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
}

.status-bes .status-amount {
  color: #d97706;
}

.status-bed .status-amount {
  color: #059669;
}

/* Form Rows */
.form-row {
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-row label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
}

.form-row input,
.form-row select,
.form-row textarea {
  padding: 10px 14px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s ease;
  width: 100%;
}

.form-row input:focus,
.form-row select:focus,
.form-row textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

.readonly-input {
  background: var(--bg) !important;
  opacity: 0.8;
  cursor: default;
}

/* Amount Input */
.amount-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.amount-input {
  flex: 1;
  padding: 10px 14px !important;
  padding-left: 50px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  text-align: center;
  direction: ltr;
}

.amount-input.readonly-amount {
  background: #fffbeb !important;
  border-color: #f59e0b !important;
  color: #d97706 !important;
}

.currency-label {
  position: absolute;
  left: 12px;
  font-size: 14px;
  color: var(--text-muted);
  font-weight: 500;
}

.amount-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-top: 4px;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
  font-size: 13px;
  color: #92400e;
}

.hint-icon {
  font-size: 16px;
}

/* Reason Select */
.reason-select {
  cursor: pointer;
}

/* Custom Reason Textarea */
.custom-reason-input {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.cancel-btn {
  padding: 10px 20px;
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: var(--hover);
}

.submit-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 480px) {
  .request-modal {
    padding: 16px;
  }

  .customer-status {
    padding: 12px;
    flex-wrap: wrap;
  }

  .status-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .status-amount {
    font-size: 16px;
  }

  .amount-input {
    font-size: 16px !important;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
    justify-content: center;
  }
}

/* Dark mode support */
[data-theme="dark"] .customer-status.status-bes {
  background: #1c1917;
  border-color: #f59e0b;
}

[data-theme="dark"] .customer-status.status-bed {
  background: #064e3b;
  border-color: #10b981;
}

[data-theme="dark"] .customer-status.status-zero {
  background: #1f2937;
  border-color: #6b7280;
}

[data-theme="dark"] .amount-hint {
  background: #1c1917;
  border-color: #f59e0b;
  color: #fbbf24;
}

[data-theme="dark"] .amount-input.readonly-amount {
  background: #1c1917 !important;
  border-color: #f59e0b !important;
  color: #fbbf24 !important;
}
.modal-footer1 {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
  direction: ltr;
}

/* Pay Money modal: keep each label beside its control. */
.pay-money-modal .form-row {
  display: grid;
  grid-template-columns: minmax(105px, 0.32fr) minmax(0, 1fr);
  align-items: center;
  column-gap: 14px;
  row-gap: 6px;
}

.pay-money-modal .form-row > label {
  margin: 0;
}

.pay-money-modal .form-row > input,
.pay-money-modal .form-row > select,
.pay-money-modal .form-row > textarea,
.pay-money-modal .form-row > .amount-input-wrapper {
  grid-column: 2;
  min-width: 0;
}

.pay-money-modal .amount-in-words,
.pay-money-modal .amount-hint {
  grid-column: 2;
}

.pay-money-modal .amount-in-words {
  margin-top: -2px;
  color: var(--text-muted, #6b7280);
  font-size: 13px;
  line-height: 1.7;
}

.pay-money-modal .form-row > textarea {
  resize: vertical;
}

@media (max-width: 480px) {
  .pay-money-modal .form-row {
    grid-template-columns: minmax(82px, 0.35fr) minmax(0, 1fr);
    column-gap: 10px;
  }
}

.cancel-btn1 {
  flex: 0 0 25%; /* 25% width */
  padding: 10px 20px;
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.cancel-btn1:hover {
  background: var(--hover);
}

.submit-btn1 {
  flex: 0 0 75%; /* 75% width */
  padding: 10px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.submit-btn1:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.submit-btn1:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
</style>
